import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  FormSectionSchema,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type McaPersonDetailsSchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["mentalCapacityAct"]["formDefinition"]["sections"][0]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function McaPersonDetailsFormSkeleton() {
  return <MultiPageFormSkeleton headerText={tk("personDetails.title")} />;
}

///

export type McaPersonDetailsFormProps<T extends WorkflowVersion> = Prettify<
  {
    workflowVersion: T;
  } & Pick<
    MultiPageFormProps<McaPersonDetailsSchema<T>>,
    | "initialValues"
    | "incidentWorkItem"
    | "onAutoSave"
    | "onSubmit"
    | "onCancel"
  >
>;

///

export function McaPersonDetailsForm<T extends WorkflowVersion>(
  props: McaPersonDetailsFormProps<T>,
) {
  const workflow = workflows[props.workflowVersion];
  const section = workflow?.["mentalCapacityAct"].formDefinition.sections.find(
    (section) => section.id === FormSectionSchema.enum.mcaPersonDetails,
  );
  const formSpecification = section?.buildFormSpecification({
    incidentWorkItem: props.incidentWorkItem,
  });
  const formSchema = formSpecification?.formSchema;
  const strictFieldSchemas = formSpecification?.strictFieldSchemas;

  const initialValuesOrDefaults: Record<string, unknown> = useMemo(() => {
    if (!section || !strictFieldSchemas || !formSchema) {
      return {};
    } else {
      return createInitialValues(strictFieldSchemas, props.initialValues);
    }
  }, [props, strictFieldSchemas, section, formSchema]);

  if (!section || !formSchema || !strictFieldSchemas) {
    return <Error />;
  }

  return (
    <MultiPageForm<typeof formSchema>
      headerText={tk("mcaPersonDetails.title")}
      incidentWorkItem={props.incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={props.onCancel}
      onSubmit={props.onSubmit}
      onAutoSave={props.onAutoSave}
    />
  );
}
